import { ChatMessage } from "@cryptors/api-client";
import { Box } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import type { CrudFilter } from "@refinedev/core";
import { IResourceComponentsProps } from "@refinedev/core";
import {
  EditButton,
  ShowButton,
  List,
  CreateButton,
  useDataGrid,
} from "@refinedev/mui";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";

import { DeleteIconButton } from "@/components/forms/DeleteIconButton";
import { SearchField } from "@/components/forms/SearchField";
import { getTranslation } from "@/utils/translation";

type ChatMessageRow = ChatMessage & { text?: string };

const buildSearchFilters = (q: string): CrudFilter[] => {
  const trimmed = q.trim();
  if (!trimmed) return [];

  const or: CrudFilter[] = [
    { field: "textTranslation.value", operator: "contains", value: trimmed },
  ];
  if (/^\d+$/.test(trimmed)) {
    or.push({ field: "id", operator: "eq", value: Number(trimmed) });
  }

  return [{ operator: "or", value: or }];
};

export const ChatMessageList: React.FC<IResourceComponentsProps> = () => {
  const { cryptorId } = useParams();
  const cryptorIdNum = Number(cryptorId);

  const { dataGridProps, setFilters } = useDataGrid<ChatMessage>({
    resource: "chat-message",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "id", order: "asc" }] },
    filters: {
      permanent: [{ field: "cryptorId", operator: "eq", value: cryptorIdNum }],
    },
  });

  const rows = useMemo(
    () =>
      (dataGridProps.rows as ChatMessage[]).map((chatMessage) => ({
        ...chatMessage,
        text: getTranslation(chatMessage.textTranslation)?.value,
      })),
    [dataGridProps.rows],
  );

  const columns = useMemo<GridColDef<ChatMessageRow>[]>(
    () => [
      {
        field: "id",
        headerName: "Id",
        type: "number",
        minWidth: 50,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "text",
        headerName: "Texte",
        sortable: false,
        flex: 1,
      },
      {
        field: "image",
        headerName: "Image",
        type: "boolean",
        sortable: false,
        width: 70,
      },
      {
        field: "video",
        headerName: "Video",
        type: "boolean",
        sortable: false,
        width: 70,
      },
      {
        field: "audio",
        headerName: "Son",
        type: "boolean",
        sortable: false,
        width: 70,
      },
      {
        field: "question",
        headerName: "Question",
        type: "boolean",
        sortable: false,
        width: 75,
      },
      {
        field: "isEntryPoint",
        headerName: "Point d'entrée",
        type: "boolean",
        minWidth: 105,
      },
      {
        field: "shouldTriggerQuiz",
        headerName: "Quiz",
        type: "boolean",
        minWidth: 105,
      },
      {
        field: "chatMessageChildrenId",
        headerName: "Enfants",
        type: "number",
        sortable: false,
        headerAlign: "center",
        align: "center",
        valueGetter: (_value, row: ChatMessage) => {
          return [
            row?.chatMessageChildrenId,
            row.question?.propositions.map((p) => p.chatMessageId),
          ]
            .flat()
            .filter((el) => el)
            .join(", ");
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.id} />
              <ShowButton hideText recordItemId={row.id} />
              <DeleteIconButton recordItemId={row.id} resource="chat-message" />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 125,
      },
    ],
    [],
  );

  return (
    <List headerButtons={<CreateButton />}>
      <Box sx={{ mb: 2 }}>
        <SearchField
          placeholder="Recherche (id ou texte)"
          buildFilters={buildSearchFilters}
          onSearch={setFilters}
        />
      </Box>
      <DataGrid
        {...dataGridProps}
        rows={rows}
        columns={columns}
        autoHeight
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </List>
  );
};
